import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ApiService } from "./api.service";
import { SortOption } from "../../shared/sort-filter/sort-filter.component";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  lat: number = 0;
  lng: number = 0;
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) { }

  public $progressSubscriber = new BehaviorSubject<number>(-1);

  latitude: number | null = null;
  longitude: number | null = null;

  getLocation(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (position) {
              const latitude = position.coords.latitude;
              const longitude = position.coords.longitude;
              this.latitude = latitude;
              this.longitude = longitude;
              resolve({ latitude, longitude });
            }
          },
          (error) => {
            console.error("Error getting location:", error);
            reject(error);
          }
        );
      } else {
        alert("Geolocation is not supported by this browser.");
        reject("Geolocation is not supported");
      }
    });
  }

  uploadTos3(body: any, showLoader: boolean = true): Observable<any> {
    const token = localStorage.getItem("authToken");
    const headers = new HttpHeaders(
      showLoader
        ? {}
        : {
          "X-No-Loader": "true",
          "x-access-token": token?.toString() || "",
        }
    );

    return this.apiService.post("files/presigned-url", body, {
      headers,
    });
  }
  getImageUrl(
    url: any,
    img: any,
    showProgress: boolean = false
  ): Observable<any> {
    const headers = new HttpHeaders(
      showProgress
        ? {}
        : {
          "X-Show-Progress": "true",
        }
    );
    return this.http.put(url, img, {
      reportProgress: true,
      observe: "events",
      headers,
    });
  }
  updateProgress(progress: number) {
    this.$progressSubscriber.next(progress); // Update progress dynamically
  }

  dynamicBindFilterOption(
    filterConfig: any[],
    data: any,
    filterConfigKey: string
  ) {
    const foundFilterConfig = filterConfig.find(
      (config) => config.key === filterConfigKey
    );
    if (foundFilterConfig) {
      foundFilterConfig.options = data;
    }
  }

  // Generic debounce function
  debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ): ((...args: Parameters<T>) => void) & { cancel: () => void } {
    let timeoutId: any;

    const debounced = (...args: Parameters<T>) => {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };

    debounced.cancel = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    return debounced;
  }

  dateFormatter(date: string, locale: string = "en-US", config?: any) {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
      ...config,
    }).format(new Date(date));
  }

  formatDDMMYYYY(date: string): string {
    // Check if date is in dd/MM/yyyy format
    const ddMMyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = date.match(ddMMyyyyPattern);

    try {
      if (match) {
        // Parse dd/MM/yyyy format manually
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
        const year = parseInt(match[3], 10);
        const formattedDate = new Date(year, month, day);
        if (isNaN(formattedDate.getTime())) {
          return "N/A";
        }
        return this.dateFormatter(formattedDate.toISOString());
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  }

  /**
   * Reads sort parameters from URL query params and finds matching sort option
   * @param params - URL query parameters object
   * @param sortOptions - Array of available sort options
   * @returns The matching SortOption or null if no match found
   */
  getSortOptionFromUrlParams(
    params: any,
    sortOptions: SortOption[]
  ): SortOption | null {
    if (params["sortBy"] && params["sortOrder"]) {
      const sortOption = sortOptions.find(
        (option) =>
          option.value === params["sortBy"] &&
          option.sortOrder === params["sortOrder"]
      );
      return sortOption || null;
    }
    return null;
  }

  /**
   * Updates URL with sort parameters
   * @param router - Angular Router instance
   * @param route - ActivatedRoute instance
   * @param sortOption - The selected sort option
   * @param additionalParams - Additional query parameters to include (optional)
   * @param replaceUrl - Whether to replace the current URL in history (default: false)
   */
  updateUrlWithSort(
    router: any,
    route: any,
    sortOption: SortOption,
    additionalParams?: any,
    replaceUrl: boolean = false
  ): void {
    const queryParams = { ...route.snapshot.queryParams, ...additionalParams };
    queryParams["sortBy"] = sortOption.value;
    queryParams["sortOrder"] = sortOption.sortOrder;

    router.navigate([], {
      relativeTo: route,
      queryParams,
      queryParamsHandling: "merge",
      replaceUrl,
    });
  }
}
